"use client";

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  claimShader,
  livingEnvironment,
  livingMode,
  livingStyle,
  releaseShader,
  scrimStyle,
  watchLivingIdle,
  type LivingBackground as Living,
  type LivingMode,
  type LivingPhones,
  type LivingScrim,
} from "@/utils/living-background";
import { startLivingShader } from "./living-shader";

const MEDIA = ["(prefers-reduced-motion: reduce)", "(max-width: 767px)"];

/**
 * The hidden layer a moving background draws in, and its pause button (WCAG 2.2.2) beside it, outside the hidden
 * layer so it can be reached. The layer is marked paused by the button and idle while off-screen or in a hidden tab;
 * living.css stops every animation in it then, and the shader stops drawing.
 */
export function LivingHost({
  className,
  attributes,
  style,
  phones,
  pause,
  place,
  children,
}: {
  className: string;
  attributes?: Record<string, string | undefined>;
  style?: CSSProperties;
  phones: LivingPhones;
  pause: boolean;
  place: "section" | "page";
  children: ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const element = host.current;
    return element ? watchLivingIdle(element, window) : undefined;
  }, []);
  return (
    <>
      <div
        ref={host}
        aria-hidden="true"
        className={className}
        {...attributes}
        data-re-living-host=""
        data-re-living-phones={phones}
        data-re-living-paused={paused ? "" : undefined}
        style={style}
      >
        {children}
      </div>
      {pause && (
        <button
          type="button"
          data-re-living-pause={place}
          aria-label={paused ? "Afspil baggrunden" : "Sæt baggrunden på pause"}
          onClick={() => setPaused((value) => !value)}
        >
          <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14">
            <path
              fill="currentColor"
              d={paused ? "M4 2.5v11l9-5.5z" : "M4 2.5h3v11H4zm5 0h3v11H9z"}
            />
          </svg>
        </button>
      )}
    </>
  );
}

/** A Levende effect (spec §9.5) inside a LivingHost: CSS fields, the shader for Silke and Bølger, or its still frame. */
export default function LivingBackground({
  living,
  scrim,
}: {
  living: Living;
  scrim?: LivingScrim;
}) {
  const element = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [mode, setMode] = useState<LivingMode>();
  const { effect, phones, quality, followPointer } = living;
  const scrimVars = scrimStyle(scrim);

  useEffect(() => {
    const owner = {};
    const queries = MEDIA.map((query) => window.matchMedia?.(query));
    const decide = () => {
      const environment = livingEnvironment(window);
      const choice = { effect, phones, quality };
      const claimed =
        livingMode(choice, { ...environment, shaderFree: true }) === "shader" &&
        claimShader(owner);
      if (!claimed) releaseShader(owner);
      setMode(livingMode(choice, { ...environment, shaderFree: claimed }));
    };
    decide();
    queries.forEach((query) => query?.addEventListener?.("change", decide));
    return () => {
      queries.forEach((query) =>
        query?.removeEventListener?.("change", decide),
      );
      releaseShader(owner);
    };
  }, [effect, phones, quality]);

  useEffect(() => {
    if (mode !== "shader" || !canvas.current) return;
    const stop = startLivingShader(canvas.current, living, () =>
      setMode("css"),
    );
    if (!stop) setMode("css");
    return stop;
    // The shader reads the settings once; a new background is a new element.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    const layer = element.current;
    if (!followPointer || !layer || !mode || mode === "still") return;
    if (!livingEnvironment(window).finePointer) return;
    let frame = 0;
    let x = 0;
    let y = 0;
    const follow = () => {
      frame = 0;
      if (layer.closest("[data-re-living-paused], [data-re-living-idle]"))
        return;
      const box = layer.getBoundingClientRect();
      const clampTo = (value: number, size: number) =>
        Math.round(Math.min(size, Math.max(-size, value)));
      layer.style.setProperty(
        "--re-living-dx",
        `${clampTo(x - box.left - box.width * 0.3, box.width)}px`,
      );
      layer.style.setProperty(
        "--re-living-dy",
        `${clampTo(y - box.top - box.height * 0.35, box.height)}px`,
      );
    };
    const move = (event: PointerEvent) => {
      x = event.clientX;
      y = event.clientY;
      frame ||= requestAnimationFrame(follow);
    };
    window.addEventListener("pointermove", move, { passive: true });
    return () => {
      window.removeEventListener("pointermove", move);
      cancelAnimationFrame(frame);
    };
  }, [followPointer, mode]);

  return (
    <div
      ref={element}
      className="re-living"
      data-re-living={effect}
      data-re-living-quality={quality}
      data-re-living-mode={mode}
      style={{ ...livingStyle(living), ...scrimVars } as CSSProperties}
    >
      <span data-re-living-field="1" />
      <span data-re-living-field="2" />
      <span data-re-living-field="3" />
      {mode === "shader" && <canvas ref={canvas} data-re-living-canvas="" />}
      {scrimVars && <span data-re-living-scrim="" />}
    </div>
  );
}
